// src/components/SpectralLinesChart.jsx
import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import {
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    Box,
    Stack,
} from '@mui/material';
import dayjs from 'dayjs';

const CHANNEL_ORDERS = {
    AS7341: ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8'],
    APDS9960: ['BLUE', 'GREEN', 'RED'],
};

const CHANNEL_WAVELENGTHS = {
    F1: '415nm', F2: '445nm', F3: '480nm', F4: '515nm',
    F5: '555nm', F6: '590nm', F7: '630nm', F8: '680nm',
    BLUE: '465nm', GREEN: '525nm', RED: '615nm',
};

function getChannelOrder(sensorType) {
    return CHANNEL_ORDERS[sensorType] || CHANNEL_ORDERS.AS7341;
}

const customChannelSort = (a, b, sortOrder) => {
    const aName = a.split('.').pop().toUpperCase();
    const bName = b.split('.').pop().toUpperCase();
    const aOrder = sortOrder[aName] ?? Infinity;
    const bOrder = sortOrder[bName] ?? Infinity;
    return aOrder - bOrder;
};

// ✅ REATORADO: Função simplificada para um gradiente linear de azul para vermelho.
const getTimeColor = (progress) => {
    const r = progress;
    const g = 0;
    const b = 1 - progress;
    return `rgb(${(r * 255) | 0}, ${(g * 255) | 0}, ${(b * 255) | 0})`;
};

const downsample = (samples, maxSamples) => {
    if (samples.length <= maxSamples) {
        return samples;
    }
    const result = [];
    const step = (samples.length - 1) / (maxSamples - 1);
    for (let i = 0; i < maxSamples; i++) {
        const index = Math.round(i * step);
        result.push(samples[index]);
    }
    return result;
};

export default function SpectralLinesChart({ chart, sensorType }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { timeSamples, channelLabels } = useMemo(() => {
        if (!chart?.labels?.length || !chart.series) {
            return { timeSamples: [], channelLabels: [] };
        }

        const seriesList = Object.values(chart.series).flat();
        if (seriesList.length === 0) {
            return { timeSamples: [], channelLabels: [] };
        }

        const channelOrder = getChannelOrder(sensorType);
        const allowedSet = new Set(channelOrder);
        const sortOrder = channelOrder.reduce((acc, ch, i) => { acc[ch] = i; return acc; }, {});

        const sortedSeries = [...seriesList]
            .sort((a, b) => customChannelSort(a.name, b.name, sortOrder))
            .filter((s) =>
                allowedSet.has(s.name.split('.').pop().toUpperCase())
            );

        const labels = sortedSeries.map((s) => {
            const ch = s.name.split('.').pop().toUpperCase();
            const wl = CHANNEL_WAVELENGTHS[ch];
            return wl ? `${ch} (${wl})` : ch;
        });

        const allSamples = chart.labels.map((timestamp, timeIndex) => {
            const values = sortedSeries.map((series) => series.data[timeIndex]);
            return { timestamp, values };
        });

        const limitedSamples = downsample(allSamples, 20);

        return { timeSamples: limitedSamples, channelLabels: labels };
    }, [chart]);

    const chartSeries = useMemo(() => {
        if (timeSamples.length === 0) return [];

        return timeSamples.map((sample, index) => {
            const progress =
                timeSamples.length > 1 ? index / (timeSamples.length - 1) : 0.5;
            return {
                name: dayjs.unix(sample.timestamp).format('DD/MM HH:mm:ss'),
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: sample.values,
                lineStyle: {
                    color: getTimeColor(progress),
                    width: 1,
                    opacity: 0.6,
                },
                emphasis: {
                    lineStyle: {
                        width: 2.5,
                        opacity: 1,
                    },
                },
            };
        });
    }, [timeSamples]);

    const option = useMemo(
        () => ({
            animation: false,
            grid: {
                left: isMobile ? '18%' : '10%',
                right: '8%',
                top: '10%',
                bottom: '20%',
            },
            tooltip: {
                trigger: 'axis',
            },
            xAxis: {
                type: 'category',
                name: 'Comprimento de Onda',
                nameLocation: 'middle',
                nameGap: 35,
                data: channelLabels,
                axisLabel: {
                    fontSize: isMobile ? 9 : 10,
                },
            },
            yAxis: {
                type: 'value',
                name: 'Amplitude / Intensidade',
                nameLocation: 'middle',
                nameGap: isMobile ? 40 : 60,
                scale: true,
                axisLabel: {
                    fontSize: isMobile ? 9 : 10,
                },
            },
            series: chartSeries,
        }),
        [chartSeries, channelLabels, isMobile]
    );

    if (timeSamples.length === 0) {
        return null;
    }

    return (
        <Paper
            elevation={3}
            sx={{
                p: { xs: 1, sm: 2 },
                pb: { xs: 2, sm: 3 },
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
        >
            <ReactECharts
                option={option}
                style={{ height: 400, width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
            />

            <Box sx={{ px: { xs: 1, sm: 4 }, mt: 2 }}>
                <Typography variant="body2" textAlign="center" gutterBottom>
                    Legenda Temporal
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        Início
                    </Typography>
                    <Box
                        sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 1,
                            // ✅ REATORADO: Gradiente simplificado de azul para vermelho.
                            background:
                                'linear-gradient(to right, rgb(0, 0, 255), rgb(255, 0, 0))',
                        }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Fim
                    </Typography>
                </Stack>
            </Box>
        </Paper>
    );
}
